import { Star } from '@qr-front/icons/clone1/star'
import { cn } from '@qr-front/ui/utils'
import { CheckoutRouteType } from 'features/purchase/purchaseState'
import { useTranslations } from 'next-intl'
import { SVGProps } from 'react'

export type TTestimonial = {
	title?: string
	description: string
	name: string
	profession: string
}

const ChatIcon = (props: SVGProps<SVGSVGElement>) => (
	<svg
		width={40}
		height={40}
		viewBox="0 0 40 40"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<path
			d="M35 5c.92 0 1.667.747 1.667 1.667V30c0 .92-.747 1.667-1.667 1.667H10.758L3.334 37.5V6.667C3.334 5.747 4.08 5 5 5zm-16.73 8.5-.745-1.147c-2.978 1.292-5.025 3.905-5.025 6.822 0 1.658.462 2.682 1.32 3.593.54.574 1.395.982 2.29.982a2.92 2.92 0 0 0 2.917-2.917c0-1.533-1.185-2.768-2.69-2.908a3.5 3.5 0 0 0-.798.017v-.154c.01-.703.153-2.721 2.423-4.11zm7.589-1.147c-2.979 1.292-5.026 3.905-5.026 6.822 0 1.658.462 2.682 1.32 3.593.54.574 1.396.982 2.29.982a2.92 2.92 0 0 0 2.917-2.917c0-1.533-1.185-2.768-2.69-2.908a3.5 3.5 0 0 0-.798.017c0-.522-.049-2.937 2.732-4.442z"
			fill="currentColor"
		/>
	</svg>
)

const ReviewerDetails = ({
	testimonial,
	alignment = 'left',
	showUserAvatar = true,
	showStars = false,
}: {
	testimonial: TTestimonial
	alignment?: 'left' | 'center'
	showUserAvatar?: boolean
	showStars?: boolean
}) => (
	<div
		className={cn(
			'mt-0 flex items-center gap-3',
			showStars && 'justify-between',
			'mb-6 items-start desktop:mb-0 desktop:mt-10',
		)}
	>
		{showUserAvatar && (
			<div className="type-body-input-title flex size-[46px] items-center justify-center rounded-full bg-white text-green">
				{testimonial.name[0]}
			</div>
		)}
		<div className={cn(alignment === 'center' && 'text-center')}>
			<div className="type-body-input-title">{testimonial.name}</div>
			<div className="type-body-small text-dark-grey">
				{testimonial.profession}
			</div>
		</div>
		{showStars && (
			<div className="flex gap-1">
				{Array.from({ length: 5 }, (_, i) => (
					<Star
						key={i}
						className="text-green size-5"
					/>
				))}
			</div>
		)}
	</div>
)

export const TestimonialCard = ({
	testimonial,
	alignment = 'left',
	showUserAvatar = true,
	showStars = false,
	className,
	routeType,
}: {
	testimonial: TTestimonial
	alignment?: 'left' | 'center'
	showUserAvatar?: boolean
	showStars?: boolean
	className?: string
	routeType?: CheckoutRouteType
}) => (
	<div
		className={cn(
			'rounded-32 flex flex-col justify-between bg-light-blue p-6 desktop:p-10 flex-col-reverse desktop:flex-col h-full',
			alignment === 'center' && 'items-center',
			className,
		)}
	>
		<div className={cn(alignment === 'center' && 'text-center')}>
			<ChatIcon
				className={cn(
					'mb-6 text-blue hidden desktop:block',
					alignment === 'center' && 'mx-auto',
					routeType === 'index' && 'hidden desktop:block',
				)}
			/>
			{testimonial.title ? (
				<h3 className="type-heading-3 desktop:hidden">{testimonial.title}</h3>
			) : null}
			<p className="type-body-medium mt-2 tracking-normal">
				{testimonial.description}
			</p>
		</div>
		<ReviewerDetails
			testimonial={testimonial}
			alignment={alignment}
			showUserAvatar={showUserAvatar}
			showStars={showStars}
		/>
	</div>
)

export const useTestimonials = (): TTestimonial[] => {
	const t = useTranslations()

	return [
		{
			title: t('public__landing__testimonials__testimonial_1__title'),
			description: t(
				'public__landing__testimonials__testimonial_1__description',
			),
			name: t('public__landing__testimonials__testimonial_1__name'),
			profession: t('public__landing__testimonials__testimonial_1__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_2__description',
			),
			name: t('public__landing__testimonials__testimonial_2__name'),
			profession: t('public__landing__testimonials__testimonial_2__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_3__description',
			),
			name: t('public__landing__testimonials__testimonial_3__name'),
			profession: t('public__landing__testimonials__testimonial_3__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_4_description',
			),
			name: t('public__landing__testimonials__testimonial_4_name'),
			profession: t('public__landing__testimonials__testimonial_4_profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_5__description',
			),
			name: t('public__landing__testimonials__testimonial_5__name'),
			profession: t('public__landing__testimonials__testimonial_5__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_6__description',
			),
			name: t('public__landing__testimonials__testimonial_6__name'),
			profession: t('public__landing__testimonials__testimonial_6__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_7__description',
			),
			name: t('public__landing__testimonials__testimonial_7__name'),
			profession: t('public__landing__testimonials__testimonial_7__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_8__description',
			),
			name: t('public__landing__testimonials__testimonial_8__name'),
			profession: t('public__landing__testimonials__testimonial_8__profession'),
		},
		{
			description: t(
				'public__landing__testimonials__testimonial_9__description',
			),
			name: t('public__landing__testimonials__testimonial_9__name'),
			profession: t('public__landing__testimonials__testimonial_9__profession'),
		},
	]
}

export const TestimonialsGrid = ({
	columns = 2,
	routeType,
	className,
}: {
	columns?: 2 | 3
	routeType?: CheckoutRouteType
	className?: string
}) => {
	const testimonialList = useTestimonials()

	const isThreeCol = columns === 3

	return (
		<div
			className={cn(
				'mt-6 grid gap-8 desktop:mt-16',
				isThreeCol ? 'desktop:grid-cols-3' : 'desktop:grid-cols-2',
				className,
			)}
		>
			{testimonialList.map((testimonial, index) => (
				<TestimonialCard
					key={testimonial.description}
					testimonial={testimonial}
					showUserAvatar={!isThreeCol}
					showStars={isThreeCol}
					className={cn(!isThreeCol && index === 0 && 'row-span-2')}
					routeType={routeType}
				/>
			))}
		</div>
	)
}
